import { useCallback, useEffect, useRef, useState } from "react";
import type { BuildPick, MediaItem, Region, RoomPage } from "@gooner/shared";
import {
  BATTLE_READY_MS,
  BATTLE_SIZE,
  BUILD_STAGES,
  COLORWAVE_SWATCH,
  GOONTOK_PRESET,
  INFINITY_DEFS,
  goontokFit,
  WALL_DEFS,
  buildInfinityPage,
  buildSourceFor,
  clipKey,
  infinityIdOfView,
  regionVolume,
  audibleCandidates,
  soundsHere,
  sourceGain,
  syncLeaderOf,
  takeDistinct,
  transposeRegions,
  ROOM_STAGE_PAGES,
} from "@gooner/shared";
import { api, apiErrorOf, userApi } from "../lib/api";
import { track } from "../analytics";
import { trackGoldGate, useGoldGate } from "../lib/gold";
import { RegionTile } from "./RegionTile";
import { crownHit } from "./RegionFapOverlay";
import { DOUBLE_TAP_MS } from "./useTapIdle";
import { useDragPaging } from "./useDragPaging";
import { useAutoScroll } from "./useAutoScroll";
import type { StageProps } from "./stages";

/**
 * INFINITY WALLS — a scroll of walls.
 *
 * Every page is one viewport: a layout dealt at random, a clip in each region, each clip
 * on a loop. Scroll (a swipe on a phone, a wheel on a desk) snaps to the next page, and
 * there is always a next page — the feed is the library at random, dealt a page at a
 * time, and a page is dealt ahead so the swipe lands on a wall that is already playing.
 *
 * THE SCROLL IS THE BROWSER'S. CSS scroll-snap does the paging — native momentum, native
 * overscroll, no gesture code and nothing to get wrong on the one platform this is for.
 * What this component adds is bookkeeping: which page is on the glass (one
 * IntersectionObserver), which pages are MOUNTED (the one on the glass and its two
 * neighbours — everything else is an empty section of the same height, so the scroll
 * position is honest and the DOM does not grow with the distance scrolled), and when to
 * deal the next one.
 *
 * SOUND IS THE BAR'S, A TAP IS NOT SOUND (owner, 2026-10-31). The bar's button sets the
 * mode and tapping the picture never changes it. The verdict per tile is the same
 * `soundsHere` every screen uses; on Play 1 the stage names the one tile on the page on
 * the glass, so every page arrives with one voice and needs no tap (see pageSolo).
 * A DOUBLE TAP FAPS: the crown's own gesture through fapBus, burst and sign-up included.
 * A single tap is left with nothing to do but wake the chrome (useTapIdle).
 *
 * ONLY THE PAGE ON THE GLASS MAKES A SOUND. A page keeps playing until it has fully left,
 * and on All every tile passes soundsHere, so without this the page you scrolled off went
 * on talking over the one you scrolled to. `active` flips at the halfway point.
 *
 * A neighbour page's players fetch and mount while off screen and do not play
 * (RegionPlayer suspends off screen), which is exactly the read-ahead a swipe needs.
 *
 * MANY SCROLLS, ONE STAGE. The row for this view id (INFINITY_DEFS) names the feed:
 * Random deals every page from the library at random; every other scroll carries a
 * `source` the stage asks the server for once per page — Enhanced's roulette (one
 * keyword, picked and kept by the server), Colorwave's colour (every garment in it),
 * Solo Girls' one-woman facets. A theme that comes back spent (the scroll has shown
 * it) is asked again for another, several times over; only a whole run of spent themes
 * deals the page from the random pool, so the scroll never stalls.
 */

/**
 * The tile that sounds on Play 1. A sound-on leader whose clip is not known silent, one a
 * page and steady for that page: indexing by the page's number moves the voice around the
 * wall from page to page without it hopping as the page re-renders.
 */
function pageSolo(regions: Region[], no: number): string | null {
  const leaders = audibleCandidates(regions);
  const voiced = leaders.filter((r) => r.source.kind !== "clip" || r.source.clip?.audible !== false);
  const from = voiced.length ? voiced : leaders;
  return from.length ? from[no % from.length].id : null;
}

/** Mounted pages either side of the one on the glass: one ahead is the read-ahead, one
 *  behind is the swipe back. */
const MOUNT_RADIUS = 1;
/** Pages dealt before the first paint, and the lead kept as the scroll advances. */
const LEAD_PAGES = 2;
/** Refill the pool from the feed below this many clips — a page needs up to six, and a
 *  refill is a round trip the deal should never wait on. */
const MIN_POOL = 12;
/** The dedupe memory. Past this many the scroll has seen more than a random page will
 *  repeat, and forgetting is cheaper than a set that grows for the life of a tab. */
const SEEN_CAP = 2000;
/**
 * GoonTok's pool is ITS feed, already drawn by weight, so a page takes the first clip —
 * or, of the next few, the one whose shape best fills the glass (a portrait clip on a
 * phone). A small window keeps the crowd's order; the whole pool would let a phone pass
 * over every landscape clip and leave them to pile up at the back.
 */
const GOONTOK_WINDOW = 3;
/**
 * How many themes a themed scroll asks for before a page gives up on a theme. The
 * server deals a theme at random and knows nothing of what this scroll has shown, so a
 * theme can come back fully seen, or in the colour the last page already wore; asking
 * again lands on another. Twelve tries across forty-odd themes is a spent CATALOG, not
 * a spent theme — only then is a page dealt from the random pool, which is the one thing
 * that keeps the scroll from stalling.
 */
const THEME_TRIES = 12;
/** A round deals four columns from this glass width up (Tailwind's `lg`); narrower is a 2×2. */
const ROUND_COLUMNS_MIN_PX = 1024;

/**
 * A Colorwave tile's frame, drawn like a neon tube: a white-hot core line, the colour
 * itself just inside it, then blooms of the colour fading inward. All inset, so the
 * tube sits on the tile's own edge and never widens the layout.
 */
const neonFrame = (glow: string): string =>
  [
    `inset 0 0 0 1px rgba(255,255,255,0.9)`,
    `inset 0 0 0 4px ${glow}`,
    `inset 0 0 10px 4px ${glow}`,
    `inset 0 0 28px 6px ${glow}cc`,
    `inset 0 0 70px 14px ${glow}55`,
  ].join(", ");

/** One page of the scroll — the shape the room mirrors (RoomPage), so a host's page IS
 *  what a member draws: `no`, the regions hung for the orientation it was dealt in, whether
 *  it was themed, a Colorwave colour, a Build round, a Goonette round. */
type Page = RoomPage;

export function InfinityStage({
  viewId,
  room,
  follow,
  registry,
  fapBus,
  syncBus,
  paused,
  soundMode,
  master,
  portrait,
  signedIn,
  onAutoMuted,
  onView,
  autoScrollSec,
  onGold,
  onPoint,
}: StageProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  // A GOON ROOM (2026-09-19). The HOST deals and scrolls exactly as on /screen and
  // mirrors every page it deals and the page on its glass (room.sendPage/sendActive);
  // a MEMBER follows: its pages are the host's, its active page the host's, its tiles
  // driven off the room's bus, and nothing here deals, picks, steps or auto-scrolls.
  const following = !!follow;
  const host = room?.role === "owner";
  const roomRef = useRef(room);
  roomRef.current = room;
  const inertRef = useRef<HTMLDivElement | null>(null);
  // A mouse grabs and pulls the scroll like a finger; Arrow/Page keys step it — and so
  // does auto-scroll, on its own clock, through the same `step`. A follower's scroll is
  // the host's: the hook is handed nothing to grab.
  const { step } = useDragPaging(following ? inertRef : rootRef);
  const [localPages, setPages] = useState<Page[]>([]);
  const [localActive, setActive] = useState(0);
  const pages: Page[] = following ? room!.stage.pages : localPages;
  const active = following ? room!.stage.active : localActive;
  /** The round tile under the pointer: lit, with "Click to select" in its middle. */
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  // The feed, in refs: the pool is consumed by deals that run from effects, and the
  // dedupe memory outlives every render.
  const poolRef = useRef<MediaItem[]>([]);
  const seenRef = useRef(new Set<string>());
  const refillingRef = useRef<Promise<void> | null>(null);
  const dealingRef = useRef(false);
  // Mode switches remount this stage (keyed on viewId). An in-flight deal must not land
  // pages — or keep THEME_TRIES fetches going — on an unmounted instance (2026-10-07 QA:
  // rapid mode switch left "Dealing the first wall…" under a reconnect race).
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);
  const nextNoRef = useRef(0);
  /** The colour of the last themed page dealt — the next one must differ (see deal). */
  const lastColorRef = useRef<string | null>(null);

  // BUILD A SLUT. The picks so far (session only — a remount is the reset), how many
  // rounds are done, the scroll's match-tier ceiling (lowered as a tier is spent), and
  // whether the one free wall has been dealt. Gold decides the flow after round five:
  // Premium scrolls without end; Basic gets one wall of her, then the ask (the card).
  const picksRef = useRef<BuildPick[]>([]);
  const [roundsDone, setRoundsDone] = useState(0);
  const roundsDoneRef = useRef(0);
  roundsDoneRef.current = roundsDone;
  const kRef = useRef<number | null>(null);
  const previewDealtRef = useRef(false);
  /** The ask, for a Basic viewer: laid OVER the wall of her the moment it is up — she is
   *  there, moving, half-seen through it, and not to be scrolled (owner, 2026-09-19) — or
   *  the moment the server refuses the scroll. Nothing is dealt past it. */
  const [paywall, setPaywall] = useState(false);
  const askPaywall = () => {
    setPaywall((cur) => {
      if (!cur) trackGoldGate("build");
      return true;
    });
  };
  const gold = useGoldGate().gold;
  const goldRef = useRef(gold);
  goldRef.current = gold;
  const portraitRef = useRef(portrait);
  portraitRef.current = portrait;
  const def = INFINITY_DEFS[infinityIdOfView(viewId) ?? "random"];
  const building = def.feed === "build" && roundsDone < BUILD_STAGES.length;
  // GOONETTE OF THE HILL. Every page is a round: the last pick stays where it was and three
  // challengers arrive; a pick is a preference, sent to the ledger. No pick before the
  // round has been on the glass BATTLE_READY_MS (a tick keeps the countdown honest).
  const battling = def.feed === "battle";
  const championRef = useRef<{ clip: MediaItem; slot: number; streak: number } | null>(null);
  const readyAtRef = useRef(new Map<number, number>());
  /** Rounds played before this sitting (a signed-in game picked up again), so the word
   *  on the glass and the ledger keep counting where the game left off. */
  const roundBaseRef = useRef(0);
  /** Signed in, the saved game loads first: nothing is dealt until it has answered. */
  const [resumed, setResumed] = useState(!battling || !signedIn || following);
  useEffect(() => {
    if (!battling || !signedIn || following) return;
    let live = true;
    api
      .battleState()
      .then((st) => {
        if (!live) return;
        if (st.champion && st.round > 0) {
          championRef.current = { clip: st.champion, slot: st.slot, streak: st.streak };
          roundBaseRef.current = st.round;
          seenRef.current.add(clipKey(st.champion));
          track("infinity_battle_resume", { scroll: def.id, round: st.round, streak: st.streak });
        }
      })
      .catch(() => undefined)
      .finally(() => { if (live) setResumed(true); });
    return () => { live = false; };
  }, [battling, signedIn]);
  const [tick, setTick] = useState(0);
  const activeRef = useRef(0);
  // The round's clock starts when EVERY tile on it is playing — not when it was dealt,
  // and not when it reached the glass: a tile's video mounts a beat after its region, and
  // a preference recorded before the four were actually moving is noise. Read off the DOM
  // every half second, which also keeps the countdown honest.
  useEffect(() => {
    if (!battling || following) return;
    const t = setInterval(() => {
      const no = activeRef.current;
      if (!readyAtRef.current.has(no)) {
        const section = rootRef.current?.querySelector(`[data-page="${no}"]`);
        const vids = section ? Array.from(section.querySelectorAll("video")) : [];
        const allPlaying = vids.length >= 2 && vids.every((v) => !v.paused && v.readyState >= 2);
        if (allPlaying) readyAtRef.current.set(no, Date.now() + BATTLE_READY_MS);
      }
      setTick((n) => n + 1);
    }, 500);
    return () => clearInterval(t);
  }, [battling]);
  const picking = building || battling;

  const refill = useCallback((): Promise<void> => {
    if (refillingRef.current) return refillingRef.current;
    // GoonTok buffers its own weighted draw the way every other scroll buffers the random
    // one: a fetch is thirty swipes, not one.
    const p = api
      .fetchSource(def.feed === "goontok" && def.source ? def.source : { kind: "random" })
      .then((res) => {
        if (seenRef.current.size > SEEN_CAP) seenRef.current = new Set();
        poolRef.current.push(...takeDistinct(res.items, seenRef.current, Infinity));
      })
      .finally(() => {
        refillingRef.current = null;
      });
    refillingRef.current = p;
    return p;
  }, [def]);

  /** Deal one more page onto the end of the scroll. Serialised: two deals in flight
   *  would draw from the same pool and number the same page. */
  const deal = useCallback(async () => {
    if (dealingRef.current) return;
    dealingRef.current = true;
    try {
      if (poolRef.current.length < MIN_POOL) {
        try {
          await refill();
        } catch {
          // A failed refill deals from what is left, and an empty pool is the one
          // failure the glass has to show (below); the next scroll retries.
        }
      }
      if (!liveRef.current) return;
      const root = rootRef.current;
      const pageAspect =
        root && root.clientHeight > 0
          ? root.clientWidth / root.clientHeight
          : typeof window !== "undefined" && window.innerHeight > 0
            ? window.innerWidth / window.innerHeight
            : 16 / 9;
      const isPortrait = portraitRef.current;
      // A themed page: the scroll's own source, fresh to this scroll. Fewer than two
      // fresh is not a wall — a spent theme — so ask again for another (THEME_TRIES);
      // only a run of spent themes falls through to the random pool for this page.
      let themed: MediaItem[] = [];
      let themedColor: string | null = null;
      // A round is the four candidates side by side — four columns on a wide desk window
      // (ROUND_COLUMNS_MIN_PX), where a column shows most of a portrait clip and so the
      // part being asked about; a 2×2 on anything narrower, phones included, where four
      // columns would be four slivers. Columns want PORTRAIT clips, and say so.
      const wide = !isPortrait && (root?.clientWidth ?? (typeof window !== "undefined" ? window.innerWidth : 0)) >= ROUND_COLUMNS_MIN_PX;
      // Build a Slut asks for the NEXT thing each page — round N+1, or the scroll of her —
      // from what has been picked so far; every other scroll asks the same source forever.
      const build = def.feed === "build";
      const goontok = def.feed === "goontok";
      // GoonTok's source is its POOL (refill), not a per-page theme.
      const source = build ? buildSourceFor(picksRef.current, kRef.current, !goldRef.current, wide) : goontok ? null : def.source;
      const round = build && source && source.kind === "build" && source.stage !== "scroll" ? source.stage : null;
      const battle = def.feed === "battle";
      // A Basic viewer's wall of her is up: the ask lies over it, nothing more is dealt.
      if (build && !round && !goldRef.current && previewDealtRef.current) {
        askPaywall();
        return;
      }
      let picks: BuildPick[] = [];
      // A round needs four fresh clips; a battle round three challengers (four the first
      // time); a wall needs two.
      const need = round ? 4 : battle ? (championRef.current ? BATTLE_SIZE - 1 : BATTLE_SIZE) : 2;
      if (source) {
        // The last theme that came back in an acceptable colour, fresh or not.
        let lastTheme: { items: MediaItem[]; color: string | null; picks: BuildPick[] } | null = null;
        for (let attempt = 0; attempt < THEME_TRIES && themed.length < need; attempt++) {
          if (!liveRef.current) return;
          try {
            const res = await api.fetchSource(source);
            if (!liveRef.current) return;
            // The same colour twice running is one long wall, not a new one: a Colorwave
            // page must change colour. Judged before the clips are marked seen, so the
            // theme is still whole when it comes round again.
            if (res.color && res.color === lastColorRef.current) continue;
            lastTheme = { items: res.items, color: res.color ?? null, picks: res.picks ?? [] };
            // Four columns want portrait challengers; a phone's 2×2 takes any shape.
            const offered = battle && wide ? res.items.filter((m) => (m.height ?? 0) > (m.width ?? 0)) : res.items;
            themed = takeDistinct(offered, seenRef.current, Infinity);
            themedColor = res.color ?? null;
            picks = res.picks ?? [];
            // The scroll of her: a spent tier means the tier below, next time.
            if (build && !round && themed.length < need && kRef.current !== 1) {
              kRef.current = Math.max(1, (kRef.current ?? picksRef.current.flatMap((p) => p.tags).length) - 1);
            }
          } catch (e) {
            // The server's paywall (a Gold lapse mid-scroll): the ask, at once, not a retry.
            if (build && apiErrorOf(e)?.goldUpsell) {
              askPaywall();
              return;
            }
            // A failed fetch is a moment (a dev server restarting, a flaky link), not a
            // verdict on the theme: the next try is a new request.
            themed = [];
          }
        }
        // Every try spent and nothing fresh: the CATALOG is spent for this scroll, not
        // the theme. A themed scroll then repeats a theme it has shown rather than
        // dealing an off-theme wall — on Colorwave an unframed page is the one thing
        // that reads as broken. Deduped within the page only; the random pool is the
        // last resort for a server that returned nothing at all.
        if (themed.length < need && lastTheme) {
          themed = takeDistinct(lastTheme.items, new Set<string>(), Infinity);
          themedColor = lastTheme.color;
          picks = lastTheme.picks;
        }
      }
      const fromTheme = themed.length >= 2;
      // A picking page is never mirrored (a mirrored pair would be one candidate twice) and
      // fewer than four (a spent catalog) deals what it has in any layout. A battle round
      // is the reigning pick plus the challengers, the pick put back in its own slot below.
      const champion = battle ? championRef.current : null;
      const roundPool = battle ? [...(champion ? [champion.clip] : []), ...themed.slice(0, champion ? BATTLE_SIZE - 1 : BATTLE_SIZE)] : themed;
      const fromPool = !(fromTheme || (battle && roundPool.length >= 2));
      const pool = !fromPool ? (battle ? roundPool : themed) : goontok ? poolRef.current.slice(0, GOONTOK_WINDOW) : poolRef.current;
      const { regions, used } = buildInfinityPage(
        nextNoRef.current,
        pool,
        {
          portrait: isPortrait,
          pageAspect,
          rng: Math.random,
          viewId: def.viewId,
          ...(round || battle ? { mirror: false, ...(pool.length >= 4 ? { preset: wide ? ("4-vertical" as const) : ("grid-2x2" as const) } : {}) } : {}),
          ...(goontok ? { mirror: false, preset: GOONTOK_PRESET } : {}),
        },
      );

      if (champion && regions.length > champion.slot) {
        // The pick stays where it was: swap its clip into its slot. Every slot on a round's
        // layout is the same shape, so the swap costs the deal nothing.
        const at = regions.findIndex((r) => r.source.kind === "clip" && r.source.clip && clipKey(r.source.clip) === clipKey(champion.clip));
        if (at >= 0 && at !== champion.slot) {
          const a = regions[at]!.source;
          regions[at] = { ...regions[at]!, source: regions[champion.slot]!.source };
          regions[champion.slot] = { ...regions[champion.slot]!, source: a };
        }
      }
      if (!liveRef.current) return;
      if (regions.length === 0) {
        setFailed(true);
        return;
      }
      setFailed(false);
      if (fromPool) {
        const usedKeys = new Set(used.map(clipKey));
        poolRef.current = poolRef.current.filter((c) => !usedKeys.has(clipKey(c)));
      }
      const no = nextNoRef.current++;
      const color = fromTheme ? themedColor : null;
      if (color) lastColorRef.current = color;
      const stage = round ? BUILD_STAGES.find((st) => st.id === round) : null;
      if (build && !round && fromTheme) {
        // The first wall of her, for anyone; for Basic it is also the last.
        if (!previewDealtRef.current) track("infinity_build_wall", { scroll: def.id, gold: goldRef.current });
        previewDealtRef.current = true;
      }
      const page: Page = {
        no,
        regions,
        portrait: isPortrait,
        themed: fromTheme,
        color,
        ...(stage ? { round: { stage: stage.id, word: stage.word, picks } } : {}),
        ...(battle ? { battle: { round: roundBaseRef.current + no + 1, championSlot: champion?.slot ?? null, streak: champion?.streak ?? 0 } } : {}),
      };
      setPages((ps) => [...ps, page]);
      // THE HOST MIRRORS the page it dealt (the hub keeps the last few for a late joiner).
      const r = roomRef.current;
      if (r && r.role === "owner") r.sendPage(page);
    } finally {
      dealingRef.current = false;
    }
  }, [refill, def]);

  // Keep LEAD_PAGES dealt beyond the one on the glass — which on mount is the first
  // pages of all. One deal per effect run; the state change it lands re-runs this.
  // Build a Slut deals one round at a time — the next only once this one is picked, so
  // nothing can be swiped past an unpicked round (there is no next section yet) — and
  // stops dealing at the paywall card, which is the last page there is.
  const ended = paywall;
  useEffect(() => {
    if (!resumed || following) return;
    if (picking) {
      if (pages.length === roundsDone) void deal();
    } else if (!ended && pages.length - 1 - active < LEAD_PAGES) void deal();
  }, [pages.length, active, deal, picking, roundsDone, ended, resumed]);

  activeRef.current = active;

  // A pick lands the next page, then the glass goes to it.
  const pendingScrollRef = useRef<number | null>(null);
  useEffect(() => {
    const to = pendingScrollRef.current;
    const root = rootRef.current;
    if (to === null || !root || !pages.some((p) => p.no === to)) return;
    pendingScrollRef.current = null;
    root.scrollTo({ top: root.clientHeight * to, behavior: "smooth" });
  }, [pages]);

  // Which page is on the glass: the section covering most of the scroller. One observer
  // over every section, rebuilt when the set of sections changes.
  useEffect(() => {
    const root = rootRef.current;
    if (following || !root || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const no = Number((e.target as HTMLElement).dataset.page);
          if (Number.isFinite(no)) setActive(no);
        }
      },
      { root, threshold: 0.5 },
    );
    for (const el of root.querySelectorAll("[data-page]")) io.observe(el);
    return () => io.disconnect();
  }, [pages.length]);

  // GOONTOK'S FIT follows the glass's CURRENT shape (goontokFit at render): a phone turned
  // sideways, or a desk window dragged narrow, re-judges fill-or-letterbox on the clip
  // already playing. Rounded so a resize drag re-renders on a real change only.
  const [glassAspect, setGlassAspect] = useState(0);
  useEffect(() => {
    if (def.feed !== "goontok") return;
    const root = rootRef.current;
    if (!root) return;
    const read = () => {
      if (root.clientHeight > 0) setGlassAspect(Math.round((root.clientWidth / root.clientHeight) * 100) / 100);
    };
    read();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(read) : null;
    ro?.observe(root);
    return () => ro?.disconnect();
  }, [def.feed]);
  // A FOLLOWER'S GLASS IS THE HOST'S PAGE: the scroller is parked on it — on every turn,
  // every page that lands (the ring shifts), every resize — and never scrolled by hand.
  useEffect(() => {
    if (!following) return;
    const root = rootRef.current;
    if (!root) return;
    const park = () => {
      const idx = pages.findIndex((p) => p.no === active);
      if (idx >= 0) root.scrollTo({ top: root.clientHeight * idx, behavior: "auto" });
    };
    park();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(park) : null;
    ro?.observe(root);
    return () => ro?.disconnect();
  }, [following, active, pages, portrait]);
  // THE HOST MIRRORS the page on its glass; and, whenever the room socket (re)connects,
  // the pages it holds and the active page again — a page is not queued while the socket
  // is down, and the first pages may well have been dealt before it opened.
  const connected = room?.connected ?? false;
  useEffect(() => {
    if (!host || !connected) return;
    room!.sendActive(active);
  }, [host, connected, active, room]);
  useEffect(() => {
    if (!host || !connected) return;
    for (const p of localPages.slice(-ROOM_STAGE_PAGES)) room!.sendPage(p);
    room!.sendActive(activeRef.current);
    // The pages themselves are mirrored as they are dealt (deal); this is the replay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host, connected]);

  // Play 1's voice: one tile of the page on the glass. The screen's mode stays senior.
  const activePage = pages.find((p) => p.no === active);
  const soloId = activePage ? pageSolo(activePage.regions, activePage.no) : null;

  // Auto-scroll: after the viewer's chosen wait on a wall, page to the next one. Set true
  // just before the automatic step so the page event below can say the swipe was ours.
  const autoRef = useRef(false);
  useAutoScroll({ sec: picking || following ? 0 : autoScrollSec, active, paused, step, onAuto: () => { autoRef.current = true; } });

  // How far people scroll is the number this surface lives or dies by. Page 0 is
  // arrival, already counted by view_played; every page after it is a swipe. Whether
  // the page was themed says whether a themed scroll's source is paying its way.
  const deepestRef = useRef(0);
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  useEffect(() => {
    deepestRef.current = Math.max(deepestRef.current, active);
    if (active > 0) {
      track("infinity_page", {
        scroll: def.id,
        page: active,
        themed: pagesRef.current.find((p) => p.no === active)?.themed ?? false,
        // Whether auto-scroll turned the page or a hand did.
        auto: autoRef.current,
      });
      autoRef.current = false;
      // The daily quest's one client-reported step (see CLIENT_BUMPABLE on the server):
      // fire-and-forget, no-op signed out, and harmless past the quest's own target —
      // the route caps what a bump can do, same trust the crown already gets.
      if (signedIn) void userApi.fapsQuestBump("scroll_pages").catch(() => undefined);
    }
  }, [active, def.id, signedIn]);

  // The sitting's summary, once: how deep and how long. On the way out and on pagehide
  // (a killed tab never runs its cleanup), the same shape view_watched uses — and the
  // same floor: under five seconds is a retarget in flight, not a sitting.
  useEffect(() => {
    const since = Date.now();
    let sent = false;
    const flush = () => {
      if (sent) return;
      const duration_sec = Math.round((Date.now() - since) / 1000);
      if (duration_sec < 5) return;
      sent = true;
      track("infinity_left", { scroll: def.id, pages: deepestRef.current + 1, duration_sec, rounds: roundsDoneRef.current });
    };
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [def.id]);

  /** A round's tap: this tile is her. Its trait tags join the profile, the round is done,
   *  and the next page (dealt by the effect above) scrolls into view when it lands. */
  const pick = (page: Page, region: Region) => {
    if (!page.round || page.no !== roundsDone) return;
    const clip = region.source.kind === "clip" ? region.source.clip : null;
    const found = clip ? page.round.picks.find((p) => p.clip === clipKey(clip)) : undefined;
    picksRef.current = [...picksRef.current, found ?? { clip: "", tags: [], label: "" }];
    setRoundsDone((n) => n + 1);
    pendingScrollRef.current = page.no + 1;
    track("infinity_build_pick", { scroll: def.id, stage: page.round.stage });
    if (picksRef.current.length === BUILD_STAGES.length) track("infinity_build_complete", { scroll: def.id, gold: goldRef.current });
  };

  /** A battle round's tap: this one stays. The other three lost to it — that is the
   *  preference, sent to the ledger — and the next round is dealt around the winner. */
  const battlePick = (page: Page, region: Region, regions: Region[]) => {
    if (!page.battle || page.no !== roundsDone) return;
    const readyAt = readyAtRef.current.get(page.no);
    if (!readyAt || Date.now() < readyAt) return;
    const clip = region.source.kind === "clip" ? region.source.clip : null;
    if (!clip) return;
    const slot = Math.max(0, regions.findIndex((r) => r.id === region.id));
    const losers = regions
      .filter((r) => r.id !== region.id && r.source.kind === "clip" && r.source.clip)
      .map((r) => clipKey((r.source as { clip: MediaItem }).clip));
    // The same clip kept is one more round held; a new winner starts at one.
    const prev = championRef.current;
    const streak = prev && clipKey(prev.clip) === clipKey(clip) ? prev.streak + 1 : 1;
    championRef.current = { clip, slot, streak };
    void api.battlePick({ winner: clipKey(clip), losers, round: page.battle.round, slot }).catch(() => undefined);
    track("infinity_battle_pick", { scroll: def.id, round: page.battle.round, kept: streak > 1, streak });
    setRoundsDone((n) => n + 1);
    pendingScrollRef.current = page.no + 1;
  };

  // Two taps on one tile, close together, are a fap. Keyed on the tile so tapping two
  // different tiles quickly is not one; cleared on the fap so a third tap starts over.
  const lastTapRef = useRef<{ id: string; at: number } | null>(null);
  const tapped = (region: Region) => {
    const now = Date.now();
    const last = lastTapRef.current;
    if (last?.id === region.id && now - last.at < DOUBLE_TAP_MS) {
      lastTapRef.current = null;
      fapBus.get(region.id)?.();
      track("infinity_double_tap_fap", { scroll: def.id });
      return;
    }
    lastTapRef.current = { id: region.id, at: now };
  };

  return (
    <div
      ref={rootRef}
      className={
        following
          ? "h-full w-full select-none snap-y snap-mandatory overflow-y-hidden overscroll-y-contain"
          : "h-full w-full cursor-grab select-none snap-y snap-mandatory overflow-y-auto overscroll-y-contain active:cursor-grabbing"
      }
      // No scrollbar: the page IS the scroll, and a thumb on a full-bleed wall is a bar
      // of chrome over video. The global thin-scrollbar rule is opted out of here.
      // pan-y ON THE SCROLLER ITSELF, not only on the glass above it: the browser reads
      // touch-action up to the nearest scroll container and stops there, so this is the
      // element whose word counts. Vertical stays the browser's (the snap scroll); a
      // sideways flick reaches the screen's channel swipe (useHorizontalSwipe).
      style={{ scrollbarWidth: "none", touchAction: "pan-y" }}
      data-infinity-stage=""
    >
      {pages.map((page) => {
        const mounted = Math.abs(page.no - active) <= MOUNT_RADIUS;
        // Dealt for one orientation, drawn in another: the same one reflection the
        // walls use (adaptive layout), applied at render and never stored.
        const hung = page.portrait === portrait ? page.regions : transposeRegions(page.regions);
        const regions =
          def.feed === "goontok" && glassAspect > 0
            ? hung.map((r) => ({ ...r, fit: goontokFit(r.source.kind === "clip" ? r.source.clip : null, glassAspect) }))
            : hung;
        // A Colorwave wall wears its colour: every tile is framed in the swatch as a neon
        // tube (neonFrame), so the page says "blue" before any clip does.
        // Inert and under the ring (z-10 vs z-20), so it never eats a tap or hides the
        // selection.
        const glow = page.color ? COLORWAVE_SWATCH[page.color] : undefined;
        const live = !following && (page.round || page.battle) && page.no === roundsDone;
        const readyAt = page.battle ? readyAtRef.current.get(page.no) : undefined;
        const waitSec = page.battle && live ? Math.max(0, Math.ceil(((readyAt ?? Date.now() + BATTLE_READY_MS) - Date.now()) / 1000)) : 0;
        void tick;
        // The ask lies over the LAST page — the wall of her a Basic viewer gets to glimpse.
        const asking = paywall && !page.round && page.no === pages[pages.length - 1]!.no;
        return (
          <section
            key={page.no}
            data-page={page.no}
            data-round={page.round?.stage}
            className="relative h-full w-full snap-start snap-always"
          >
            {/* THE WORD. A round says what is being chosen, in the middle of the four —
                where the Reddit originals put it — and NOTHING else: no trait words on the
                tiles (owner, 2026-09-19), the picture is the choice. Inert, so every tap
                reaches a tile. */}
            {mounted && asking ? (
              <PaywallCard
                onGold={() => onGold("build")}
                onRandom={() => {
                  // A coin between the two walls that need no building: Colorwave, or the
                  // Community Wall. Not "start over" (owner, 2026-09-19): someone who will
                  // not pay is sent somewhere that plays, not back to five more taps.
                  const to = Math.random() < 0.5 ? INFINITY_DEFS.colorwave : WALL_DEFS.main;
                  track("infinity_build_leave", { scroll: def.id, to: to.id });
                  onPoint(to.viewId);
                }}
              />
            ) : null}
            {mounted && (page.round || page.battle) ? (
              <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
                <span
                  className="flex flex-col items-center rounded-md bg-[var(--color-accent)] px-3 py-1 text-black shadow-[0_0_24px_rgba(0,0,0,0.6)]"
                  style={{ transform: "rotate(-4deg)" }}
                >
                  {page.round ? (
                    <>
                      <span className="text-[10px] font-bold uppercase tracking-[0.3em] sm:text-xs">Choose your</span>
                      <span className="text-2xl font-black uppercase tracking-widest sm:text-4xl">{page.round.word}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-2xl font-black uppercase tracking-widest sm:text-4xl">Round {page.battle!.round}</span>
                      {waitSec > 0 ? <span className="text-[10px] font-bold uppercase tracking-[0.3em] sm:text-xs">watch · {waitSec}</span> : null}
                    </>
                  )}
                </span>
              </div>
            ) : null}
            {mounted
              ? regions.map((region) => (
                  <RegionTile
                    key={region.id}
                    region={region}
                    // A mirrored follower (see infinityMirrorPairs) plays its leader flipped.
                    leader={syncLeaderOf(region, regions)}
                    driven={follow ?? undefined}
                    registry={registry}
                    syncBus={syncBus}
                    paused={paused}
                    muted={page.no !== active || !soundsHere(soundMode, region, soloId)}
                    volume={sourceGain(master, regionVolume(region))}
                    onAutoMuted={onAutoMuted}
                    onView={onView}
                    // Every clip a page carries was dealt from /source/fetch (the pool, a
                    // theme, a build) — already past the ban filter, so no echo is owed.
                    vetted
                    selected={false}
                    signedIn={signedIn}
                    fapBus={fapBus}
                    fapSurface="infinity"
                    onFapped={room ? (id) => room.sendFap(id) : undefined}
                    onMouseEnter={live ? () => setHoverId(region.id) : undefined}
                    onMouseLeave={live ? () => setHoverId((h) => (h === region.id ? null : h)) : undefined}
                    // The whole gesture here: a double tap faps. The crown stops its own
                    // click, and its marker keeps it out of this on the one path
                    // stopPropagation cannot cover (see crownHit).
                    onClick={(e) => {
                      if (crownHit(e.target)) return;
                      // On the round being played, a tap is the pick and ONLY the pick
                      // (owner, 2026-09-19).
                      if (live) {
                        setHoverId(null);
                        if (page.battle) battlePick(page, region, regions);
                        else pick(page, region);
                        return;
                      }
                      // A follower shows its leader's clip: a tap on it is a tap on its leader.
                      tapped(syncLeaderOf(region, regions) ?? region);
                    }}
                  >
                    {/* THE DEFENDING CHAMPION wears a trophy and the rounds she has held, so
                        the one that stayed is never mistaken for a challenger. Bottom-left:
                        the crown has bottom-right, and the view-name chip hangs over the
                        top of the middle columns. */}
                    {page.battle && page.battle.championSlot !== null && regions.indexOf(region) === page.battle.championSlot ? (
                      <span
                        aria-label={`Defending champion, ${page.battle.streak} round${page.battle.streak === 1 ? "" : "s"}`}
                        data-battle-champion={page.battle.streak}
                        className="pointer-events-none absolute bottom-3 left-3 z-10 flex items-center gap-1 rounded-full bg-[var(--color-accent)] px-2.5 py-1 text-sm font-bold text-black shadow-lg"
                      >
                        <span aria-hidden>🏆</span>
                        {page.battle.streak}
                      </span>
                    ) : null}
                    {/* A round tile under the pointer: lit a little, and a grey bubble in its
                        middle saying what a click does (owner, 2026-09-19). Inert, so the
                        click lands on the tile beneath. */}
                    {live && hoverId === region.id ? (
                      <span aria-hidden data-round-hover="" className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/10 ring-2 ring-inset ring-white/60">
                        <span className="rounded-full bg-zinc-800/90 px-3 py-1.5 text-xs font-semibold text-zinc-100 shadow-lg backdrop-blur">
                          {page.battle && waitSec > 0 ? `Watch ${waitSec}s to pick` : page.battle ? "Tap to keep her" : "Click to select"}
                        </span>
                      </span>
                    ) : null}
                    {glow ? (
                      <span
                        aria-hidden
                        data-colorwave-glow={page.color}
                        className="pointer-events-none absolute inset-0 z-10"
                        style={{ boxShadow: neonFrame(glow) }}
                      />
                    ) : null}
                  </RegionTile>
                ))
              : null}
          </section>
        );
      })}
      {pages.length === 0 ? (
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-zinc-500">
            {failed ? (
              <span className="text-sm tracking-wide">Nothing to deal — check the connection.</span>
            ) : (
              <>
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
                <span className="text-sm tracking-wide">{following ? "Waiting for the host's wall…" : "Dealing the first wall…"}</span>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The ask, laid OVER the wall of her a Basic viewer gets — translucent, so she is there
 * behind it, moving, half-seen, and that is the whole pitch (owner, 2026-09-19): "you
 * built her; there's a lot more of her." No chips, no numbers, no tier talk beyond the
 * one line. The button opens the Premium pitch in the build gate's voice (the host's
 * dialog); "Go to a Random View" is the other door — a coin between Colorwave and the
 * Community Wall — for someone who will not pay: somewhere that plays, not five more taps.
 */
function PaywallCard({ onGold, onRandom }: { onGold: () => void; onRandom: () => void }) {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 p-6 backdrop-blur-[1.5px]" data-build-paywall="">
      <div className="flex max-w-md flex-col items-center gap-4 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-accent-soft)]">You built her</p>
        <h2 className="text-3xl font-bold text-zinc-100 drop-shadow">There&apos;s a lot more of her.</h2>
        <p className="text-sm text-zinc-300">Unlimited scrolling of your favorite girls is Premium.</p>
        <button
          type="button"
          onClick={onGold}
          className="rounded-full bg-[var(--color-accent)] px-5 py-2.5 text-sm font-bold text-black shadow-lg transition-colors hover:bg-[var(--color-accent)]/85"
        >
          Go Premium
        </button>
        <button type="button" onClick={onRandom} className="text-xs text-zinc-400 underline-offset-2 hover:text-zinc-200 hover:underline">
          Go to a Random View
        </button>
      </div>
    </div>
  );
}
